import React, { useState } from "react";
import { makeStyles, useTheme } from "@mui/styles";
import { useDispatch } from "react-redux";
import {
  TextField,
  Grid,
  Typography,
  Checkbox,
  TextareaAutosize,
  FormHelperText,
  Switch,
  Tooltip,
  IconButton,
  Fade,
  useMediaQuery,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Help";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import NumberInput from "./NumberInput";

import { showFileUploadAction } from "../../../redux/users/actions";
import ImageInput from "../EditProfile/ImageInput";
import { green } from "../../../constants/colors";
import { useLocation } from "react-router";

const useStyles = makeStyles((theme) => ({
  autoReplayContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-end",

    [theme.breakpoints.down("md")]: {
      marginTop: "14px",
      flexWrap: "nowrap",
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  textField: {
    [theme.breakpoints.down("md")]: {
      marginTop: "40px",
    },
  },
  rootSW: {
    width: 42,
    height: 26,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.switch.circle,
      "& + $track": {
        backgroundColor: theme.palette.switch.bg,
        opacity: 1,
        border: "none",
      },
    },
    "&$focusVisible $thumb": {
      color: "yellow",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 24,
    height: 24,
    backgroundColor: theme.palette.switch.circle,
  },
  track: {
    borderRadius: 26 / 2,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.switch.track,
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
  detailTitle: {
    fontSize: "14px",
    fontFamily: "SFProText-Regular",
  },
  textInput: {
    borderRadius: "16px",
    backgroundColor: "inherit",
    border: "1px solid #DEDEDE",
    paddingLeft: "18px",
    overflow: "hidden",
    "& .MuiInputBase-input": {
      padding: "14px 0 !important",
    },
    "& .MuiInputBase-input.Mui-disabled": {
      backgroundColor: theme.palette.disableInputFiledSettings,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      outline: "transparent",
    },
  },
  checkbox: {
    paddingLeft: 0,
    "& .MuiTouchRipple-root span": {
      backgroundColor: "transparent !important",
    },
    "&:hover": {
      backgroundColor: "transparent",
    },
    "& .MuiButtonBase-root": {
      backgroundColor: "none",
    },
  },
  textarea: {
    fontSize: "16px",
    width: "100% !important",
    borderColor: "rgb(196 196 196)",
    marginTop: "5%",
    borderRadius: "16px",
    backgroundColor: theme.palette.bg,
    color: theme.palette.mainText,
    padding: "10px 0 0 10px",
    fontFamily: theme.typography.fontFamily,
    "&:focus": {
      // border: `2px solid ${theme.palette.primary.main}`,
      outlineColor: theme.palette.primary.main,
    },
    "&:hover": {
      borderColor: "black !important",
    },
  },
  textareaAlert: { color: "#B00020" },
  username: {
    color: "#535559",
    fontWeight: 600,
    fontSize: "18px",
  },
  unChangedInput: {
    backgroundColor: theme.palette.bg,
  },
  changePictureText: {
    fontSize: "13px",
    color: green,
    fontWeight: "bold",
    "&:hover": {
      cursor: "pointer",
    },
  },
  numberInput: {},
  test: { color: "red" },
  fullNameAndChangeImgProfile: (props) => ({
    display: "flex",
    marginLeft: "22px",
    [theme.breakpoints.down("lg")]: {
      justifyContent: "space-between",
    },
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
    },
  }),
}));

const useButtonStyles = makeStyles((theme) => ({
  root: {
    width: 25,
    height: 25,
    padding: 2,
    backgroundColor: theme.palette.bg,
    "&:hover": {
      backgroundColor: theme.palette.divider, // Set your desired hover background color here
    },
  },
}));

const InputField = ({
  id,
  name,
  label,
  value,
  auto_reminder_msg_every,
  handleEveryChange,
  onChange,
  error,
  checkbox,
  textarea,
  setAutoReply,
  setSecondAutoReply,
  setAutoReminder,
  imageInput,
  switchInput,
  first_name,
  last_name,
  setFieldValue,
  avatarURL,
  type,
  handleChangeSwitch,
  switchChecked,
  placeholder,
  setSubscriptionWelcomeMsg,
  CheckboxDisabledSetAutoReminderMsg,
  withHint,
  hintText,
  setAutoStop,
  maxLength = 0,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const buttonClasses = useButtonStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isAutoReplayPage = location.pathname.includes("auto-replies");
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const isLg = useMediaQuery(theme.breakpoints.down("lg"));

  const classes = useStyles({ isAutoReplayPage });

  const [length, setLength] = useState(() => value?.length || 0);

  return (
    <Grid
      item
      container
      xs={12}
      sx={{ justifyContent: "space-between" }}
      alignItems="center"
      className={isMd && isAutoReplayPage && classes.autoReplayContainer}
    >
      {/* Grid1: label or image input */}
      <Grid
        item={true}
        xs={isMd ? 12 : 2}
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        {imageInput ? (
          <ImageInput
            avatarURL={avatarURL}
            setFieldValue={(filedName, file) => {
              setFieldValue(filedName, file);
            }}
            errorMessage={error || ""}
            field={{ name: "avatar_url" }}
          />
        ) : (
          <Typography className={classes.detailTitle}>{label}</Typography>
        )}
        {withHint ? (
          <Tooltip
            followCursor
            title={hintText}
            placement="top"
            arrow
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 600 }}
          >
            <IconButton className={buttonClasses.root}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        ) : null}
        {checkbox ? (
          setAutoReply === true || setAutoReply === false ? (
            <Switch
              style={{
                color: setAutoReply ? green : "gray",
              }}
              checked={setAutoReply}
              onChange={onChange}
              inputProps={{ "aria-label": "primary checkbox" }}
              name="set_auto_reply_msg"
              id="set_auto_reply_msg"
              classes={{
                root: classes.rootSW,
                switchBase: classes.switchBase,
                thumb: classes.thumb,
                track: classes.track,
                checked: classes.checked,
              }}
            />
          ) : setSecondAutoReply === true || setSecondAutoReply === false ? (
            <Switch
              style={{
                color: setSecondAutoReply ? green : "gray",
              }}
              checked={setSecondAutoReply}
              onChange={onChange}
              inputProps={{ "aria-label": "primary checkbox" }}
              name="set_second_auto_reply_msg"
              id="set_second_auto_reply_msg"
              classes={{
                root: classes.rootSW,
                switchBase: classes.switchBase,
                thumb: classes.thumb,
                track: classes.track,
                checked: classes.checked,
              }}
            />
          ) : setAutoStop === true || setAutoStop === false ? (
            <Switch
              style={{
                // color: setAutoReminder ? green : "gray" //this is when we need to change the business logic // this in the previous version ( allow the user to disable the auto reminder msg)
                color: green,
              }}
              disabled
              checked
              inputProps={{ "aria-label": "primary checkbox" }}
              name="set_auto_reminder_msg"
              id="set_auto_reminder_msg"
              classes={{
                root: classes.rootSW,
                switchBase: classes.switchBase,
                thumb: classes.thumb,
                track: classes.track,
                checked: classes.checked,
              }}
            />
          ) : setSubscriptionWelcomeMsg === true || setSubscriptionWelcomeMsg === false ? (
            <Switch
              style={{
                color: setSubscriptionWelcomeMsg ? green : "gray",
              }}
              checked={setSubscriptionWelcomeMsg}
              onChange={onChange}
              name="set_subscription_welcome_msg"
              id="set_subscription_welcome_msg"
              classes={{
                root: classes.rootSW,
                switchBase: classes.switchBase,
                thumb: classes.thumb,
                track: classes.track,
                checked: classes.checked,
              }}
            />
          ) : (
            <Switch
              style={{
                color: setAutoReminder ? green : "gray",
              }}
              disabled={CheckboxDisabledSetAutoReminderMsg}
              checked={setAutoReminder}
              onChange={onChange}
              inputProps={{ "aria-label": "primary checkbox" }}
              name="set_auto_reminder_msg"
              id="set_auto_reminder_msg"
              classes={{
                root: classes.rootSW,
                switchBase: classes.switchBase,
                thumb: classes.thumb,
                track: classes.track,
                checked: classes.checked,
              }}
            />
          )
        ) : null}
      </Grid>

      {/* Grid2: different type of inputs */}
      <Grid item={true} xs={isMd ? 0 : isLg ? 8 : 10} style={{ width: isMd && "100%" }}>
        <div className={classes.fullNameAndChangeImgProfile}>
          {imageInput ? (
            <div>
              <Typography className={classes.username}>
                {first_name} {last_name}
              </Typography>
              <Typography
                className={classes.changePictureText}
                onClick={() => dispatch(showFileUploadAction(true))}
              >
                {t("Change Profile Picture")}
              </Typography>
            </div>
          ) : textarea ? (
            setAutoReply === true ||
            setAutoReply === false ||
            setSubscriptionWelcomeMsg === false ||
            setSubscriptionWelcomeMsg === true ? (
              <div style={{ flexGrow: 1, position: "relative" }}>
                <TextareaAutosize
                  minRows={4}
                  maxRows={12}
                  aria-label="maximum height"
                  placeholder={label}
                  defaultValue=""
                  disabled={
                    !!(checkbox && (setAutoReply === false || setSubscriptionWelcomeMsg === false))
                  }
                  required
                  type={type}
                  id={id}
                  name={name}
                  className={classnames(classes.textarea)}
                  onChange={(e) => {
                    if (maxLength > 0) {
                      setLength(e.target.value.toString().length);
                    }
                    onChange(e);
                  }}
                  value={value || ""}
                  error={!!error}
                  helperText={error || ""}
                  maxLength={maxLength > 0 ? maxLength : Infinity}
                />
                {maxLength ? (
                  <span
                    style={{
                      position: "absolute",
                      zIndex: 2,
                      top: 35,
                      right: 10,
                      fontFamily: "SFProText-Regular",
                      minWidth: "50px",
                      backgroundColor: theme.palette.bg,
                      width: "auto",
                      fontSize: 13,
                    }}
                  >
                    {`${length} / ${maxLength}`}
                  </span>
                ) : (
                  ""
                )}
                <FormHelperText className={classes.textareaAlert}>{error || ""}</FormHelperText>
              </div>
            ) : setSecondAutoReply === true || setSecondAutoReply === false ? (
              <div style={{ flexGrow: 1 }}>
                <TextareaAutosize
                  minRows={4}
                  maxRows={12}
                  aria-label="maximum height"
                  placeholder={label}
                  defaultValue=""
                  disabled={!!(checkbox && setSecondAutoReply === false)}
                  required
                  type={type}
                  id={id}
                  name={name}
                  className={classnames(classes.textarea)}
                  onChange={onChange}
                  value={value || ""}
                  error={!!error}
                  helperText={error || ""}
                />
                <FormHelperText className={classes.textareaAlert}>{error || ""}</FormHelperText>
              </div>
            ) : setAutoStop === true || setAutoStop === false ? (
              <div style={{ flexGrow: 1 }}>
                <TextareaAutosize
                  minRows={4}
                  maxRows={12}
                  aria-label="maximum height"
                  placeholder={label}
                  defaultValue=""
                  // disabled={!!(checkbox && setAutoStop === false)}
                  disabled={false}
                  required
                  type={type}
                  id={id}
                  name={name}
                  className={classnames(classes.textarea)}
                  onChange={onChange}
                  value={value || ""}
                  error={!!error}
                  helperText={error || ""}
                />
                <FormHelperText className={classes.textareaAlert}>{error || ""}</FormHelperText>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
                <div>
                  <TextareaAutosize
                    minRows={4}
                    maxRows={12}
                    aria-label="maximum height"
                    placeholder={label}
                    defaultValue=""
                    disabled={!!(checkbox && setAutoReminder === false)}
                    required
                    type={type}
                    id={id}
                    name={name}
                    className={classnames(classes.textarea)}
                    onChange={onChange}
                    value={value || ""}
                    error={!!error}
                    helperText={error || ""}
                  />
                  <FormHelperText className={classes.textareaAlert}>{error || ""}</FormHelperText>
                </div>
                <NumberInput
                  disabled={!!(checkbox && setAutoReminder === false)}
                  value={auto_reminder_msg_every}
                  setValue={handleEveryChange}
                />
              </div>
            )
          ) : switchInput ? (
            <Switch
              checked={switchChecked}
              onChange={({ target: { checked } }) => handleChangeSwitch(checked)}
              color="primary"
              name="privacy"
              inputProps={{ "aria-label": "primary checkbox" }}
            />
          ) : (
            <TextField
              disabled={
                !!(checkbox && (setAutoReply === false || setSubscriptionWelcomeMsg === false)) ||
                id === "twilio_number" ||
                id === "subscription_link"
              }
              style={{
                marginTop: isMd && "0.5rem",
                background:
                  id === "twilio_number" || id === "subscription_link"
                    ? theme.palette.disableInputFiledSettings
                    : "inherit",
              }}
              margin={isMd ? "none" : "normal"}
              // className={classes.textField}
              required
              placeholder={placeholder}
              type={type}
              fullWidth
              id={id}
              name={name}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                endAdornment: maxLength ? (
                  <span
                    style={{
                      marginRight: 5,
                      minWidth: "65px",
                      width: "auto",
                      fontSize: 13,
                      fontFamily: "SFProText-Regular",
                    }}
                  >{`${length} / ${maxLength}`}</span>
                ) : (
                  <></>
                ),
              }}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              inputProps={{
                maxLength: maxLength ? maxLength : null,
              }}
              onInput={(e) => {
                if (maxLength !== 0) {
                  setLength(e.target.value.toString().length);
                }
              }}
              className={classnames(classes.textInput)}
              onChange={(e) => {
                onChange(e);
              }}
              value={value || ""}
              error={!!error}
              rows={name === "auto_reply_msg" ? 2 : 1}
              maxRows={name === "auto_reply_msg" ? 8 : 1}
              multiline={name === "auto_reply_msg"}
              helperText={error || ""}
            />
          )}
        </div>
      </Grid>
    </Grid>
  );
};

export default InputField;
